import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import './App.css';

function App() {
  const [message, setMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialPrompt, setInitialPrompt] = useState('Ask me anything about optimization or 1st Optimal.');
  const [error, setError] = useState('');

  const transcriptRef = useRef(null);
  const inputRef = useRef(null);
  const followReply = useRef(true);
  const inFlight = useRef(false);
  const controllerRef = useRef(null);
  const containedEmbed = useRef(false);
  const [newReply, setNewReply] = useState(false);
  const scrollToBottom = useCallback(() => {
    const transcript = transcriptRef.current;
    if (transcript) transcript.scrollTop = transcript.scrollHeight;
    followReply.current = true;
    setNewReply(false);
  }, []);
  useEffect(() => {
    if (followReply.current) scrollToBottom();
    else if (chatHistory[chatHistory.length - 1]?.sender === 'Optimus') setNewReply(true);
  }, [chatHistory, loading, error, scrollToBottom]);

  useLayoutEffect(() => {
    const parentOrigins = new Set(['https://1stoptimal.com', 'https://www.1stoptimal.com']);
    const resize = () => {
      const embedded = window.parent !== window;
      const height = embedded ? window.innerHeight : (window.visualViewport?.height ?? window.innerHeight);
      // The existing WordPress block allocates a full-height iframe below its
      // header and clips its bottom. Reserve 72px (observed 47px desktop header,
      // plus room for a wrapped mobile title) until a contained launcher opts in.
      const available = embedded && !containedEmbed.current ? Math.max(0, height - 72) : height;
      document.documentElement.style.setProperty('--chat-height', `${available}px`);
    };
    const focusComposer = (event) => {
      if (event.source === window.parent && parentOrigins.has(event.origin) && event.data?.type === 'optimus:focus') {
        if (event.data.layout === 'contained') { containedEmbed.current = true; resize(); }
        inputRef.current?.focus({preventScroll: true});
      }
    };
    const dismiss = (event) => {
      if (event.key === 'Escape' && window.parent !== window) {
        parentOrigins.forEach((origin) => window.parent.postMessage({type: 'optimus:close'}, origin));
      }
    };
    window.addEventListener('message', focusComposer);
    window.addEventListener('keydown', dismiss);
    window.addEventListener('resize', resize);
    window.visualViewport?.addEventListener('resize', resize);
    resize();
    parentOrigins.forEach((origin) => {
      if (window.parent !== window) window.parent.postMessage({type: 'optimus:ready'}, origin);
    });
    return () => {
      window.removeEventListener('message', focusComposer);
      window.removeEventListener('keydown', dismiss);
      window.removeEventListener('resize', resize);
      window.visualViewport?.removeEventListener('resize', resize);
      document.documentElement.style.removeProperty('--chat-height');
      controllerRef.current?.abort();
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (message.trim() === '' || inFlight.current) return;

    inFlight.current = true;
    followReply.current = true;
    setLoading(true);
    setError('');

    const userMessage = { sender: 'You', text: message };
    setChatHistory((prevHistory) => [...prevHistory, userMessage]); // Add user's message to chat history

    const controller = new AbortController();
    controllerRef.current = controller;
    const timeout = setTimeout(() => controller.abort(), 95000);
    try {
    const res = await fetch('/api/chat', {
      method: 'POST',
      credentials: 'same-origin',
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ prompt: message }),
    });

    const data = await res.json().catch(() => null);
    if (!res.ok || !data || typeof data.response !== 'string') {
      throw new Error(res.status === 429 ? 'Optimus is busy. Please wait a minute and try again.' : 'Optimus could not respond right now. Please try again shortly.');
    }
    const botMessage = {
      sender: 'Optimus',
      text: formatResponse(typeof data.answer === 'string' ? data.answer : data.response),
      links: actionLinks(data),
    };

    setChatHistory((prevHistory) => [...prevHistory, botMessage]); // Add only bot's response to chat history
    setMessage(''); // Clear input field after sending
    setInitialPrompt('');  // Clears the initial prompt after first input
    } catch (err) {
      setError(err.name === 'AbortError' ? 'That request took too long. Please try again.' : (err.message && err.message.startsWith('Optimus') ? err.message : 'Connection interrupted. Please try again.'));
    } finally {
      clearTimeout(timeout);
      inFlight.current = false;
      controllerRef.current = null;
      setLoading(false);
    }
  };

  // Function to format URLs and emails in the bot's response
  const formatResponse = (text) => {
    const pattern = /(https?:\/\/[^\s<>"']+|[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})/gi;
    return text.split(pattern).map((part, index) => {
      if (/^https?:\/\//i.test(part)) {
        const url = part.replace(/[.,;!\])]+$/, '');
        return <React.Fragment key={index}><a href={url} target="_blank" rel="noopener noreferrer">{url}</a>{part.slice(url.length)}</React.Fragment>;
      }
      if (/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(part)) {
        return <a key={index} href={`mailto:${part}`}>{part}</a>;
      }
      return part;
    });
  };

  // Match the reviewed test bot: readable action labels, separate from answer text.
  const actionLinks = (data) => {
    const records = [...(Array.isArray(data.actions) ? data.actions : []), ...(Array.isArray(data.resources) ? data.resources : [])];
    const seen = new Set();
    return records.filter(Boolean).flatMap((item) => [
      { href: item.url, label: item.label },
      { href: item.email ? `mailto:${item.email}` : null, label: item.email },
      { href: item.phone ? `tel:${String(item.phone).replace(/[^+0-9]/g, '')}` : null, label: item.phone },
    ]).filter((link) => {
      if (typeof link.href !== 'string' || typeof link.label !== 'string' || !/^(https?:\/\/|mailto:|tel:)/i.test(link.href) || seen.has(link.href)) return false;
      seen.add(link.href);
      return true;
    });
  };

  // Function to render each message
  const renderMessage = (message, index) => {
    const isUser = message.sender === 'You';

    return (
      <div key={index} className={`message ${isUser ? 'user-message' : 'bot-message'}`}>
        <div className="message-content">
          <span className="sr-only">{message.sender}: </span>
          <span style={{whiteSpace: 'pre-wrap'}}>{message.text}</span>
          {message.links?.length > 0 && <div className="message-actions">
            {message.links.map((link) => <a key={link.href} href={link.href} target="_blank" rel="noopener noreferrer">{link.label}</a>)}
          </div>}
        </div>
      </div>
    );
  };

  return (
    <div className="App">
      <main>
        <div className="chat-container" ref={transcriptRef} role="log" aria-label="Conversation with Optimus" aria-live="polite" aria-relevant="additions" tabIndex={0}
          onScroll={() => {
            const t = transcriptRef.current;
            followReply.current = t.scrollHeight - t.scrollTop - t.clientHeight < 48;
            if (followReply.current) setNewReply(false);
          }}>
          {initialPrompt && <div className="initial-prompt">{initialPrompt}</div>}
          {chatHistory.map((message, index) => renderMessage(message, index))}
        </div>
        {newReply && <button className="new-reply" type="button" onClick={scrollToBottom}>New reply ↓</button>}
        <div className="chat-status" role="status" aria-live="polite">{loading ? 'Optimus is thinking…' : ''}</div>
        {error && <div className="chat-error" role="alert">{error}</div>}
        <form onSubmit={handleSubmit} className="input-form">
          <label className="sr-only" htmlFor="chat-message">Message Optimus</label>
          <input
            id="chat-message"
            ref={inputRef}
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Type your message here..."
            maxLength={4000}
            readOnly={loading}
            aria-busy={loading}
            autoComplete="off"
            enterKeyHint="send"
          />
          <button type="submit" disabled={loading}>
            {loading ? 'Sending...' : 'Send'}
          </button>
        </form>
      </main>
    </div>
  );
}

export default App;
